import { useCallback, useEffect, useRef, useState } from 'react';
import { useBillingClient } from 'client/client-hook';
import { featureFlags } from 'env';
import useAuth from 'hooks/useAuth';
import { analytics } from 'lib/analytics';
import { subscriptionSummary } from 'lib/analytics/subscription-value';
import type { LibSubscription } from 'lib/billing/pbAdapter';
import { FreezeQuote } from './types';
import { withTimeout } from './withTimeout';

// Shorter than the status refresh: a customer waits on this before the cancel flow starts.
const PAUSE_OFFER_TIMEOUT_MS = 6_000;

// `unavailable` is a refusal with its reasons, so they can be told why; a failed check is `none`, and says nothing.
export type PauseOffer =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'ready'; quote: FreezeQuote }
  | { status: 'unavailable'; quote: FreezeQuote };

const LOADING: PauseOffer = { status: 'loading' };
const NONE: PauseOffer = { status: 'none' };

/**
 * Whether a cancel can be turned into a pause, as billing-manager decides; any failure offers none.
 * `reload` re-asks after billing moved, keeping the current offer on screen until the answer lands.
 */
export function usePauseOffer(subscriptions: LibSubscription[]): { offer: PauseOffer; reload: () => void } {
  const { user } = useAuth() as any;
  const userId: string = user?.tcId || '';
  const enabled = featureFlags.subscriptionFreeze && Boolean(userId);
  const [offer, setOffer] = useState<PauseOffer>(enabled ? LOADING : NONE);
  const [asked, setAsked] = useState(0);
  const getBillingClient = useBillingClient();
  // The client and the plans are read once per visit; their identities change on every render.
  const latest = useRef({ getBillingClient, subscriptions });

  useEffect(() => {
    if (!enabled) {
      setOffer(NONE);
      return;
    }
    let stale = false;
    (async () => {
      try {
        const client = await latest.current.getBillingClient();
        const quote = (await withTimeout(client.previewSubscriptionFreeze({ userId }), PAUSE_OFFER_TIMEOUT_MS)) as FreezeQuote;
        if (stale) return;
        analytics.freezePreviewed({
          ...subscriptionSummary(latest.current.subscriptions),
          duration_days: quote.durationDays,
          eligible: quote.eligible,
          ineligible_reasons: quote.ineligibleReasonCodesList
        });
        // A pause is only offered with the day it ends, so every screen after can name it.
        const complete = quote.eligible && quote.durationDays > 0 && Boolean(quote.plannedResumeAt?.seconds);
        const refused = !quote.eligible && quote.ineligibleReasonCodesList.length > 0;
        setOffer(complete ? { status: 'ready', quote } : refused ? { status: 'unavailable', quote } : NONE);
      } catch {
        if (!stale) setOffer(NONE);
      }
    })();
    return () => {
      stale = true;
    };
  }, [enabled, userId, asked]);

  const reload = useCallback(() => setAsked((count) => count + 1), []);
  return { offer, reload };
}
